// GeneRisk.js
import React, { useState, useEffect, useRef } from 'react';
import './wsgenerisk.css';
import { ensureGeneRiskProCookie } from './WebserverUser';
import { getApiEndpoint, authHeaders } from './config/generisk-config';
import GenomicsReport from '../../SampleManager/OmicsView/reports/GenomicsReport';
import baseLogoLight from '../../base_logo_light.png';

const CHUNK_SIZE = 55 * 1024 * 1024; // 55MB

function GeneRisk() {
  // cookie/session
  const [cookieId, setCookieId] = useState('');
  const [daysLeft, setDaysLeft] = useState(0);
const dbg = (...args) => console.log('[GeneRisk]', ...args);
const currentFileRef = useRef(null);
const uploadingGuardRef = useRef(false); // prevents double starts

  // ui state
  const [settingsOpen, setSettingsOpen] = useState(false);
const [dragOver, setDragOver] = useState(false);
  // upload state
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMsg, setStatusMsg] = useState('');
  const abortRef = useRef(null);

  // jobs state
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);

  // cookie bootstrap
  useEffect(() => {
    const cookieInfo = ensureGeneRiskProCookie();
    if (cookieInfo) {
      const { id, expiry } = cookieInfo;
      setCookieId(id);
      const msPerDay = 1000 * 60 * 60 * 24;
      setDaysLeft(Math.max(0, Math.ceil((expiry - Date.now()) / msPerDay)));
    }
  }, []);

  // poll jobs every 5s
  useEffect(() => {
    if (!cookieId) return;
    let stop = false;

    const fetchJobs = async () => {
      try {
        const res = await fetch(getApiEndpoint('jobs'), {
          headers: authHeaders(cookieId),
        });
        if (!res.ok) throw new Error(`jobs fetch failed: ${res.status}`);
        const data = await res.json();
        if (!stop) {
          setJobs(data);
          // keep selection pinned if it still exists
          if (selectedJob) {
            const stillHere = data.find(j => j.jobUuid === selectedJob.jobUuid);
            if (!stillHere) {
              setSelectedJob(null);
            } else {
              // auto-refresh selected job reference
              if (selectedJob.status !== 'Completed' && stillHere.status === 'Completed') {
                setSelectedJob(stillHere);
              } else {
                setSelectedJob(stillHere);
              }
            }
          }
        }
      } catch (e) {
        // silent-ish fail
      }
    };

    fetchJobs();
    const t = setInterval(fetchJobs, 5000);
    return () => { stop = true; clearInterval(t); };
  }, [cookieId, selectedJob]);

  const cookieTooltip = cookieId
    ? `Cookie ID: ${cookieId}\nDays left: ${daysLeft}`
    : 'Loading cookie information...';


  // ---------- Upload helpers ----------
  const uploadChunk = async (fileObj, chunkIndex, uploadKey, controller) => {
    const start = chunkIndex * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, fileObj.size);
    const chunk = fileObj.slice(start, end);

    const formData = new FormData();
    formData.append('uploadKey', uploadKey);
    formData.append('chunkIndex', String(chunkIndex));
    formData.append('chunk', chunk);

    const res = await fetch(getApiEndpoint('uploadChunkSimple'), {
      method: 'POST',
      headers: authHeaders(cookieId),
      body: formData,
      signal: controller.signal
    });
    if (!res.ok) throw new Error(`Chunk ${chunkIndex} failed (${res.status})`);
    return res.json();
  };

const handleStartUpload = async (initialFile) => {
  const useFile = initialFile || currentFileRef.current || file;

  if (uploadingGuardRef.current) {
    dbg('handleStartUpload:bail:guarded');
    return;
  }
  if (!useFile || !cookieId) {
    dbg('handleStartUpload:bail', { hasFile: !!useFile, hasCookie: !!cookieId });
    return;
  }
  uploadingGuardRef.current = true;


  const controller = new AbortController();
  abortRef.current = controller;

const uploadKeyR1 = `${Date.now()}_${Math.random().toString(36).slice(2)}_R1`;
const totalChunks = Math.ceil(useFile.size / CHUNK_SIZE);
dbg('handleStartUpload:begin', {
  fileName: useFile.name,
  fileSize: useFile.size,
  totalChunks,
  uploadKeyR1
});

  setIsUploading(true);
  setStatusMsg('Uploading…');
  setProgress(0);
  setSelectedJob(null);

  try {
    for (let i = 0; i < totalChunks; i++) {
await uploadChunk(useFile, i, uploadKeyR1, controller);
      const pct = Math.round(((i + 1) / totalChunks) * 100);
      setProgress(pct);
      dbg('handleStartUpload:progress', { chunkIndex: i, pct });
    }

    setStatusMsg('Finalizing…');
    const finalizeBody = {
      uploadKeyR1,
      uploadKeyR2: null,
      totalChunks,
metadata: { originalFileName: useFile.name }
    };
    dbg('finalize:request', finalizeBody);

    let finRes;
    try {
      finRes = await fetch(getApiEndpoint('finalizeUploadSimple'), {
        method: 'POST',
        headers: { ...authHeaders(cookieId), 'Content-Type': 'application/json' },
        body: JSON.stringify(finalizeBody),
        signal: controller.signal
      });
    } catch (err) {
      dbg('finalize:network-error', err);
      throw err;
    }

    const finText = await finRes.text().catch(() => '');
    dbg('finalize:response', { status: finRes.status, body: finText.slice(0, 500) });

    if (!finRes.ok) {
      throw new Error(`Finalize failed (${finRes.status}) ${finText || ''}`);
    }

    setStatusMsg('Queued. It will start shortly.');
    setFile(null);
    currentFileRef.current = null;

  } catch (err) {
    if (err.name === 'AbortError') {
      setStatusMsg('Canceled.');
      dbg('handleStartUpload:aborted');
    } else {
      setStatusMsg(`Error: ${err.message}`);
      dbg('handleStartUpload:error', err);
    }
} finally {
  setIsUploading(false);
  abortRef.current = null;
  uploadingGuardRef.current = false;
  dbg('handleStartUpload:end');
}

};

const handleCancel = () => {
  if (abortRef.current) {
    abortRef.current.abort();
    abortRef.current = null;
  }
  currentFileRef.current = null;
  uploadingGuardRef.current = false;
  dbg('cancel:done');
};


  const handleBackToUpload = () => {
    setSelectedJob(null);
  };

  // ---------- Render helpers ----------
  const renderJobProgressBar = (pct) => {
    if (pct == null || Number.isNaN(pct)) {
      return null;
    }
    const clamped = Math.max(0, Math.min(100, pct));
    return (
      <div className="ws-generisk-job-progress" aria-hidden="true">
        <div className="ws-generisk-job-progress-inner" style={{ width: `${clamped}%` }} />
      </div>
    );
  };

  const renderSidebarJobs = () => (
    <div className="ws-generisk-jobs-list">
      <div className="ws-generisk-jobs-heading">
        <span className="ws-generisk-jobs-title">Jobs</span>
        {jobs.length > 0 && (
          <span className="ws-generisk-jobs-count">{jobs.length}</span>
        )}
      </div>
      <div className="ws-generisk-jobs-scroll">
        {jobs.length === 0 ? (
          <p className="ws-generisk-jobs-empty">
            No jobs yet. Upload a file to get started.
          </p>
        ) : (
          jobs.map((job) => (
            <button
              key={job.jobUuid}
              type="button"
              className={`ws-generisk-job-item ${selectedJob?.jobUuid === job.jobUuid ? 'active' : ''}`}
              onClick={() => setSelectedJob(job)}
            >
              <div className="ws-generisk-job-top">
                <span className="ws-generisk-job-name">{job.resultCode || 'Pending job'}</span>
                <span className={`ws-generisk-job-status ws-status-${(job.status || 'queued').toLowerCase()}`}>
                  {job.status || 'Queued'}
                </span>
              </div>
              <div className="ws-generisk-job-meta">
                <span>{job.submissionTime ? new Date(job.submissionTime).toLocaleString() : 'Submitted N/A'}</span>
              </div>
              {renderJobProgressBar(job.progress)}
            </button>
          ))
        )}
      </div>
    </div>
  );

const renderMainUploadPanel = () => {
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.name.endsWith('.vcf') || droppedFile.name.endsWith('.vcf.gz') || droppedFile.name.endsWith('.gz'))) {
currentFileRef.current = droppedFile;
setFile(droppedFile);
// Auto-start with explicit file
setTimeout(() => {
  if (cookieId && droppedFile && !isUploading) {
    handleStartUpload(droppedFile);
  }
}, 50);

    }
  };

const handleFileSelect = (e) => {
  if (isUploading) return; // avoid double starts
  const inputEl = e.currentTarget;
  const selectedFile = inputEl.files && inputEl.files[0];
  // Reset input so selecting the same file again will trigger onChange next time
  inputEl.value = '';

  if (!selectedFile) return;

  // Accept .vcf, .vcf.gz, .gz (case-insensitive)
  const name = selectedFile.name.toLowerCase();
  const ok =
    name.endsWith('.vcf') ||
    name.endsWith('.vcf.gz') ||
    name.endsWith('.gz');

  if (!ok) {
    setStatusMsg('Unsupported file type. Please choose .vcf or .vcf.gz');
    return;
  }

currentFileRef.current = selectedFile;
setFile(selectedFile);

// Auto-start with explicit file
setTimeout(() => {
  if (cookieId && !isUploading) handleStartUpload(selectedFile);
}, 50);

};


  return (
    <section className="ws-generisk-layout-main-panel">
      <div className="ws-generisk-upload-card">
        <div 
          className={`ws-generisk-dropzone ${dragOver ? 'dragover' : ''} ${isUploading ? 'uploading' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="ws-generisk-dropzone-content">
            {isUploading ? (
              <>
                <div className="ws-generisk-upload-icon">⚡</div>
                <h3 className="ws-generisk-upload-title">Uploading...</h3>
                <p className="ws-generisk-upload-description">
                  {file?.name} - {statusMsg}
                </p>
                
                <div className="ws-generisk-upload-progress">
                  <div className="ws-generisk-progress-bar">
                    <div
                      className="ws-generisk-progress"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="ws-generisk-progress-text">{progress}%</div>
                </div>
                
                <button 
                  type="button" 
                  className="ws-generisk-cancel-button" 
                  onClick={handleCancel}
                >
                  Cancel Upload
                </button>
              </>
            ) : (
              <>
                <h3 className="ws-generisk-upload-title">Drop VCF file here</h3>
                <p className="ws-generisk-upload-description">
                  or click to browse for .vcf or .vcf.gz files
                </p>
                
   <input
  type="file"
  accept=".vcf,.vcf.gz,.gz"
  onClick={(e) => { e.currentTarget.value = ''; }}  // ensure same file re-triggers onChange
  onChange={handleFileSelect}
  disabled={isUploading}
  className="ws-generisk-file-input-hidden"
  id="file-input"
/>

                <label htmlFor="file-input" className="ws-generisk-browse-button">
                  Browse Files
                </label>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
  const renderMainJobDetails = () => {
    if (!selectedJob) return renderMainUploadPanel();

    const {
      resultCode,
      patientId,
      analysisCode,
      sampleCode,
      subprojectId,
      status,
      progress: p,
      submissionTime,
      initiatedTime,
      completedTime,
      originalFiles
    } = selectedJob;

    const formatTimestamp = (value) => (value ? new Date(value).toLocaleString() : 'N/A');

    return (
      <section className="ws-generisk-layout-main-panel">
        <div className="ws-generisk-detail-card">
          <div className="ws-generisk-detail-header">
            <div className="ws-generisk-detail-title-section">
              <h3 className="ws-generisk-detail-title">{resultCode || 'GeneRisk job'}</h3>
              <span className={`ws-generisk-badge ws-status-${(status || 'queued').toLowerCase()}`}>
                {status || 'Queued'}
              </span>
            </div>
            <button type="button" className="ws-generisk-back-button" onClick={handleBackToUpload}>
              ← Back to Upload
            </button>
          </div>

          {renderJobProgressBar(p)}

          <div className="ws-generisk-detail-grid">
            <div className="ws-generisk-detail-item">
              <span className="ws-generisk-detail-label">Patient</span>
              <span className="ws-generisk-detail-value">{patientId || 'N/A'}</span>
            </div>
            <div className="ws-generisk-detail-item">
              <span className="ws-generisk-detail-label">Analysis</span>
              <span className="ws-generisk-detail-value">{analysisCode || 'N/A'}</span>
            </div>
            <div className="ws-generisk-detail-item">
              <span className="ws-generisk-detail-label">Sample</span>
              <span className="ws-generisk-detail-value">{sampleCode || 'N/A'}</span>
            </div>
            <div className="ws-generisk-detail-item">
              <span className="ws-generisk-detail-label">Subproject</span>
              <span className="ws-generisk-detail-value">{subprojectId || 'N/A'}</span>
            </div>
            <div className="ws-generisk-detail-item">
              <span className="ws-generisk-detail-label">Submitted</span>
              <span className="ws-generisk-detail-value">{formatTimestamp(submissionTime)}</span>
            </div>
            <div className="ws-generisk-detail-item">
              <span className="ws-generisk-detail-label">Initiated</span>
              <span className="ws-generisk-detail-value">{formatTimestamp(initiatedTime)}</span>
            </div>
            <div className="ws-generisk-detail-item">
              <span className="ws-generisk-detail-label">Completed</span>
              <span className="ws-generisk-detail-value">{formatTimestamp(completedTime)}</span>
            </div>
          </div>

          <div className="ws-generisk-detail-files">
            <span className="ws-generisk-detail-label">Original files</span>
            <span className="ws-generisk-detail-value ws-generisk-mono">
              {(() => {
                try {
                  const v = typeof originalFiles === 'string' ? JSON.parse(originalFiles) : originalFiles;
                  if (Array.isArray(v)) return v.join(', ');
                } catch (_) {
                  // ignore parse errors
                }
                return String(originalFiles || 'N/A');
              })()}
            </span>
          </div>
        </div>
      </section>
    );
  };

  // When a job is selected and Completed, show the GenomicsReport for that patient
  const renderSelectedDataPanel = () => {
    if (!selectedJob) return renderMainUploadPanel();

    const statusKey = (selectedJob.status || '').toLowerCase();

    if (statusKey === 'complete' || statusKey === 'completed') {
      const prefixedId = `${selectedJob.sampleCode}${selectedJob.analysisCode}_${selectedJob.patientId}`;

      return (
        <section className="ws-generisk-layout-main-panel">
          <div className="ws-generisk-genomics-header">
            <button 
              type="button" 
              className="ws-generisk-back-button"
              onClick={handleBackToUpload}
            >
              ← Back
            </button>
          </div>
          <GenomicsReport
            id={prefixedId}
            type="genome"
            data={{ patientId: prefixedId, sampleId: prefixedId }}
          />
        </section>
      );
    }

    return renderMainJobDetails();
  };

  return (
    <div className="ws-generisk-layout-root">
      <aside className="ws-generisk-layout-sidebar">
        <div className="ws-generisk-layout-sidebar-top">
<a href="/">
  <img
    src={baseLogoLight}
    alt="Home"
    className="ws-generisk-layout-logo-image"
  />
</a>
     <a
       href="https://xiyasong.github.io/Genomics_platform/Quick_start_for_GenRiskPro/"
       target="_blank"
       rel="noopener noreferrer"
       className="ws-generisk-layout-help"
     >
       Help
     </a>

        </div>

        <div className="ws-generisk-layout-sidebar-content">
          {renderSidebarJobs()}
        </div>

        <div className="ws-generisk-layout-sidebar-bottom">
          <button
            type="button"
            className="ws-generisk-layout-settings-button"
            onClick={() => setSettingsOpen(true)}
          >
            Settings
          </button>
          <div className="ws-generisk-layout-cookie-wrapper" title={cookieTooltip}>
            <i className="fas fa-cookie-bite"></i>
          </div>
        </div>
      </aside>

      <main className="ws-generisk-layout-content">
        <header className="ws-generisk-layout-content-header">
          <h1 className="ws-generisk-layout-heading">GenRiskPro Analysis Platform</h1>
          <p className="ws-generisk-layout-description">
            Upload and analyze genomic data with advanced risk assessment tools.
          </p>
        </header>

        {renderSelectedDataPanel()}
      </main>

      {settingsOpen && (
        <div
          className="ws-generisk-layout-settings-overlay"
          role="presentation"
          onClick={() => setSettingsOpen(false)}
        >
          <div
            className="ws-generisk-layout-settings-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Settings"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="ws-generisk-layout-settings-header">
              <h2 className="ws-generisk-layout-settings-title">Settings</h2>
              <button
                type="button"
                className="ws-generisk-layout-close-button"
                onClick={() => setSettingsOpen(false)}
                aria-label="Close settings"
              >
                ×
              </button>
            </div>
            <div className="ws-generisk-layout-settings-body">
              <div className="ws-generisk-layout-settings-item">
                <span className="ws-generisk-layout-settings-label">Interface Theme</span>
                <span className="ws-generisk-layout-settings-value">Light mode (default)</span>
              </div>
              <div className="ws-generisk-layout-settings-item">
                <span className="ws-generisk-layout-settings-label">Session Status</span>
                <span className="ws-generisk-layout-settings-value">Active ({daysLeft} days remaining)</span>
              </div>
            </div>
          </div>
      </div>        )}

    </div>
  );
}

export default GeneRisk;